import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    Tooltip,
    Paper,
    Alert,
    Button,
    Typography,
} from '@mui/material';
import {
    FastRewind,
    FastForward,
    ZoomOut,
    ZoomIn,
    PlayArrow,
    Pause
} from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
import TimelinePlugin from 'wavesurfer.js/plugins/timeline';
import Hover from 'wavesurfer.js/plugins/hover';
import RegionsPlugin from 'wavesurfer.js/plugins/regions';
import { AuditioBackendManager } from './AuditioBackend';
import { useShortcut } from './Shortcuts';
import SnippetsActionsAPI from './SnippetsActionsAPI';
import { HHMMSSToSeconds, secondsToHHMMSS } from './TimeUtils';
import PlaybackSpeedDropdown from './components/PlaybackSpeedDropdown';
import { useAuth } from './contexts/AuthContext';
import { metricsService } from './services/MetricsService';

// Props interface for the WaveformAPI component
type WaveformAPIProps = {
    auditioBackendManager: AuditioBackendManager,
    regions: any,
    wavesurferRef: any,
    regionRef: any,
    setRenderTrigger: React.Dispatch<React.SetStateAction<number>>,
    audioFileId: number,
    renderTrigger: number,
    ownerId?: number | null;
    onWaveformReadyChange?: (isReady: boolean) => void;
    onWaveformLoadingChange?: (isLoading: boolean) => void;
};

const WaveformAPI: React.FC<WaveformAPIProps> = ({
    auditioBackendManager,
    regions,
    wavesurferRef,
    regionRef,
    setRenderTrigger,
    audioFileId,
    renderTrigger,
    ownerId,
    onWaveformReadyChange,
    onWaveformLoadingChange
}) => {
    const { user, firebaseUser } = useAuth();
    
    // Store isOwner in both state (for rendering) and ref (for callbacks)
    const [isOwner, setIsOwner] = useState<boolean>(false);
    const isOwnerRef = useRef<boolean>(false);
    
    // State variables for waveform functionality
    const [waveformIsReady, setWaveformIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loopOption, setLoopOption] = useState('');
    const loopOptionRef = useRef('');
    const isRestartingRegionRef = useRef(false); // Prevent infinite loops in region-out handler
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const snippetsCacheRef = useRef<{ audioFileId: number, snippets: any[], timestamp: number } | null>(null);
    const SNIPPETS_CACHE_TTL = 30000; // Cache for 30 seconds
    const [playPause, setPlayPause] = useState(false);
    const [minZoomLevel, setMinZoomLevel] = useState(0.01);
    const [zoomLevel, setZoomLevel] = useState(20);
    const [playbackRate, setPlaybackRate] = useState(1.0);
    const [startMarker, setStartMarker] = useState<any | null>(null);
    const [endMarker, setEndMarker] = useState<any | null>(null);
    const [drivePermissionError, setDrivePermissionError] = useState<{
        driveFileId?: string, 
        driveFileUrl?: string,
        errorMessage?: string,
        isAuthError?: boolean,
        isAuthCancelled?: boolean,
        requiresReauth?: boolean
    } | null>(null);

    // Touch gesture zoom state variables
    const [initialDistance, setInitialDistance] = useState<number | null>(null);
    const [initialZoom, setInitialZoom] = useState<number | null>(null);
    const [isZooming, setIsZooming] = useState(false);
    const [showTouchHint, setShowTouchHint] = useState(false);
    const waveformContainerRef = useRef<HTMLDivElement>(null);

    const BASE64_MARKER = ';base64,';

    // Calculate distance between two touch points
    const getDistance = (touch1: Touch, touch2: Touch): number => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    // Calculate center point between two touch points
    const getCenter = (touch1: Touch, touch2: Touch): { x: number; y: number } => {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    };

    // Handle touch start for zoom gestures
    const handleTouchStart = (e: TouchEvent) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const distance = getDistance(e.touches[0], e.touches[1]);
            setInitialDistance(distance);
            setInitialZoom(zoomLevel);
            setIsZooming(true);
        }
    };

    // Handle touch move for zoom gestures
    const handleTouchMove = (e: TouchEvent) => {
        if (e.touches.length === 2 && isZooming && initialDistance !== null && initialZoom !== null) {
            e.preventDefault();
            const currentDistance = getDistance(e.touches[0], e.touches[1]);
            const scale = currentDistance / initialDistance;
            const newZoom = Math.max(minZoomLevel, Math.min(200, initialZoom * scale));
            
            setZoomLevel(newZoom);
            if (wavesurferRef.current) {
                wavesurferRef.current.zoom(newZoom);
            }

            // Add visual feedback
            const container = waveformContainerRef.current;
            if (container) {
                container.classList.add('zooming');
            }
        }
    };

    // Handle touch end for zoom gestures
    const handleTouchEnd = (e: TouchEvent) => {
        if (e.touches.length < 2) {
            setIsZooming(false);
            setInitialDistance(null);
            setInitialZoom(null);
            
            // Remove visual feedback
            const container = waveformContainerRef.current;
            if (container) {
                container.classList.remove('zooming');
            }
        }
    };

    // Handle wheel events for Mac trackpad zoom gestures
    const handleWheel = (e: WheelEvent) => {
        // Check if it's a pinch gesture (deltaY will be 0 for pinch gestures)
        if (Math.abs(e.deltaY) < 1 && Math.abs(e.deltaX) < 1) {
            e.preventDefault();
            // This is likely a pinch gesture, handle it via touch events
            return;
        }
        
        // Handle scroll wheel zoom with Ctrl/Cmd key
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const zoomDelta = e.deltaY > 0 ? -5 : 5;
            const newZoom = Math.max(minZoomLevel, Math.min(200, zoomLevel + zoomDelta));
            setZoomLevel(newZoom);
            if (wavesurferRef.current) {
                wavesurferRef.current.zoom(newZoom);
            }
        }
    };

    // Handle gesture events for Mac trackpad (alternative approach)
    const handleGestureStart = (e: any) => {
        e.preventDefault();
        setInitialZoom(zoomLevel);
        setIsZooming(true);
    };

    const handleGestureChange = (e: any) => {
        e.preventDefault();
        if (initialZoom !== null) {
            const scale = e.scale;
            const newZoom = Math.max(minZoomLevel, Math.min(200, initialZoom * scale));
            setZoomLevel(newZoom);
            if (wavesurferRef.current) {
                wavesurferRef.current.zoom(newZoom);
            }
        }
    };

    const handleGestureEnd = (e: any) => {
        e.preventDefault();
        setIsZooming(false);
        setInitialZoom(null);
        
        // Remove visual feedback
        const container = waveformContainerRef.current;
        if (container) {
            container.classList.remove('zooming');
        }
    };

    // Set up touch gesture event listeners
    useEffect(() => {
        const container = waveformContainerRef.current;
        if (!container) return;

        // Add touch event listeners
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: false });

        // Add wheel event listener for Mac trackpad gestures
        container.addEventListener('wheel', handleWheel, { passive: false });

        // Add gesture event listeners for Mac trackpad (Safari)
        container.addEventListener('gesturestart', handleGestureStart, { passive: false });
        container.addEventListener('gesturechange', handleGestureChange, { passive: false });
        container.addEventListener('gestureend', handleGestureEnd, { passive: false });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('wheel', handleWheel);
            container.removeEventListener('gesturestart', handleGestureStart);
            container.removeEventListener('gesturechange', handleGestureChange);
            container.removeEventListener('gestureend', handleGestureEnd);
        };
    }, [zoomLevel, minZoomLevel, isZooming, initialDistance, initialZoom]);

    // Show touch hint on mobile devices
    useEffect(() => {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isTouchDevice = 'ontouchstart' in window;
        
        if ((isMobile || isTouchDevice) && waveformIsReady) {
            setShowTouchHint(true);
            const timer = setTimeout(() => {
                setShowTouchHint(false);
            }, 3000); // Show for 3 seconds
            
            return () => clearTimeout(timer);
        }
    }, [waveformIsReady]);

    // Convert data URI to binary data
    const convertDataURIToBinary = (dataURI: string) => {
        let base64Data;
        
        // Check if it's already in data URI format
        if (dataURI.startsWith('data:')) {
            const base64Index = dataURI.indexOf(BASE64_MARKER);
            if (base64Index === -1) {
                throw new Error('Invalid data URI format');
            }
            base64Data = dataURI.substring(base64Index + BASE64_MARKER.length);
        } else {
            // Assume it's raw base64 data
            base64Data = dataURI;
        }

        try {
            const raw = window.atob(base64Data);
            const rawLength = raw.length;
            const array = new Uint8Array(new ArrayBuffer(rawLength));

            for(let i = 0; i < rawLength; i++) {
                array[i] = raw.charCodeAt(i);
            }
            return array;
        } catch (error) {
            console.error('Error decoding base64 data:', error);
            throw new Error('Invalid base64 data');
        }
    };

    // Convert audio data to URL
    const readAudioDataAsURL = (audioData: ArrayBuffer | Blob): string => {
        const audioBlob = audioData instanceof Blob ? audioData : new Blob([audioData], { type: 'audio/mpeg' });
        return URL.createObjectURL(audioBlob);
    };

    // Helper function to apply styling to region content
    const applyRegionContentStyling = (region: any) => {
        if (region.element) {
            const regionContent = region.element.querySelector('[part="region-content"]');
            if (regionContent) {
                (regionContent as HTMLElement).style.color = '#ffffff';
                (regionContent as HTMLElement).style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
                (regionContent as HTMLElement).style.fontWeight = '500';
                (regionContent as HTMLElement).style.fontSize = '0.9rem';
            }
        }
    };

    // Helper function to set up a MutationObserver for a region to watch for content changes
    const setupRegionContentObserver = (region: any) => {
        if (region.element) {
            // Create a MutationObserver to watch for changes to the region content
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' || mutation.type === 'characterData') {
                        applyRegionContentStyling(region);
                    }
                });
            });

            // Start observing the region element for changes
            observer.observe(region.element, {
                childList: true,
                subtree: true,
                characterData: true
            });

            // Store the observer on the region so we can disconnect it later if needed
            region._contentObserver = observer;
        }
    };

    // Helper function to apply styling to all regions
    const applyStylingToAllRegions = () => {
        if (!regions.current) return;
        
        Object.values(regions.current).forEach((region: any) => {
            if (region && region.element) {
                applyRegionContentStyling(region);
            }
        });
    };

    // Helper function to update region colors based on active state
    const updateRegionColors = (activeRegionId: string | null) => {
        Object.values(regions.current).forEach((region: any) => {
            if (!region || !region.setOptions) {
                console.log("Skipping invalid region:", region);
                return;
            }

            try {
                if (region.id === activeRegionId) {
                    // Active region - coral color
                    region.setOptions({
                        color: 'rgba(252, 185, 170, 0.25)'
                    });
                    if (region.element && region.element.style) {
                        region.element.style.backgroundColor = 'rgba(252, 185, 170, 0.25)';
                        region.element.style.borderColor = 'rgba(252, 185, 170, 0.5)';
                        region.element.style.boxShadow = '0 0 20px rgba(252, 185, 170, 0.3)';
                    }
                } else {
                    // Inactive region - teal color
                    region.setOptions({
                        color: 'rgba(85, 203, 205, 0.15)'
                    });
                    if (region.element && region.element.style) {
                        region.element.style.backgroundColor = 'rgba(85, 203, 205, 0.15)';
                        region.element.style.borderColor = 'rgba(85, 203, 205, 0.4)';
                        region.element.style.boxShadow = '0 0 15px rgba(85, 203, 205, 0.2)';
                    }
                }
            } catch (error) {
                console.error("Error updating region colors for region:", region.id, error);
            }
        });
    };

    // Effect to update region drag/resize settings when waveform is ready
    // Note: Ownership is determined at load time, so this mainly handles initial setup
    useEffect(() => {
        if (waveformIsReady && wavesurferRef.current && regions.current && Object.keys(regions.current).length > 0) {
            const currentIsOwner = isOwnerRef.current;
            console.log('🔍 WaveformAPI - Updating region drag/resize settings, isOwner:', currentIsOwner);
            Object.values(regions.current).forEach((region: any) => {
                if (region && region.setOptions) {
                    region.setOptions({
                        drag: currentIsOwner,
                        resize: currentIsOwner,
                        contentEditable: currentIsOwner
                    });
                }
            });
        }
    }, [waveformIsReady]);

    // Effect to periodically apply styling to all regions
    useEffect(() => {
        if (waveformIsReady && regions.current && Object.keys(regions.current).length > 0) {
            const interval = setInterval(() => {
                try {
                    // Update colors based on current active region
                    updateRegionColors(regionRef.current?.id || null);
                    applyStylingToAllRegions();
                } catch (error) {
                    console.error("Error in periodic styling update:", error);
                }
            }, 100); // Check every 100ms for more responsive styling

            return () => clearInterval(interval);
        }
    }, [waveformIsReady]);

    // Effect to ensure all regions are visible after waveform is ready
    useEffect(() => {
        if (waveformIsReady && regions.current && Object.keys(regions.current).length > 0) {
            // Add a delay to ensure all regions are created
            const timeoutId = setTimeout(() => {
                try {
                    if (wavesurferRef.current) {
                        const newZoom = auditioBackendManager.ensureRegionVisibility(wavesurferRef.current, zoomLevel, 30);
                        if (newZoom !== null) {
                            setZoomLevel(newZoom);
                            console.log('🔍 Post-load zoom adjustment applied:', newZoom);
                        }
                    }
                } catch (error) {
                    console.warn('Error in post-load region visibility check:', error);
                }
            }, 500); // Wait 500ms after waveform is ready
            
            return () => clearTimeout(timeoutId);
        }
    }, [waveformIsReady]);

    // Effect to handle renderTrigger changes for UI updates (without destroying waveform)
    useEffect(() => {
        if (waveformIsReady && regions.current && Object.keys(regions.current).length > 0) {
            // Apply styling and update colors when renderTrigger changes
            try {
                updateRegionColors(regionRef.current?.id || null);
                applyStylingToAllRegions();
            } catch (error) {
                console.error("Error in renderTrigger update:", error);
            }
        }
    }, [renderTrigger]);

    // Load the uploaded audio file into wavesurfer
    const loadAudio = async () => {
        try {
            // Destroy existing WaveSurfer instance if it exists
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
            }

            // Create plugins
            const timeline = TimelinePlugin.create({
                style: {
                    fontSize: '15px',
                    color: 'rgb(146, 161, 195)',
                },
            });

            const hover = Hover.create({
                lineColor: '#92A1C3',
                lineWidth: 2,
                labelBackground: '#555',
                labelColor: '#fff',
                labelSize: '11px',
            });

            // Create the regions plugin
            const regionPlugin = RegionsPlugin.create();

            // Get the specific audio file by ID instead of the current active file
            const audioFile = await auditioBackendManager.getAudioFileById(audioFileId);
            
            if (!audioFile) {
                console.error('No audio file found for ID:', audioFileId);
                return;
            }

            // Extract ownerId from file data and calculate isOwner immediately
            const fileOwnerId = audioFile.ownerId;
            const calculatedIsOwner = fileOwnerId === null
                ? true  // No owner - everyone can edit
                : Number(user?.id) === Number(fileOwnerId);  // Check if current user is the owner (type-safe comparison)
            
            // Store in both state (for rendering) and ref (for callbacks)
            setIsOwner(calculatedIsOwner);
            isOwnerRef.current = calculatedIsOwner;
            
            console.log('🔍 WaveformAPI - Calculated ownership from file data:', {
                fileOwnerId,
                userId: user?.id,
                calculatedIsOwner
            });

            // Explicitly set the current audio file
            // auditioBackendManager.setCurrentAudioFile(audioFile);

            let audioData: string | null;
            try {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'WaveformAPI.tsx:510',message:'loadAudio: About to call getAudioData',data:{fileId:audioFile.id,driveFileId:audioFile.driveFileId,userId:user?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
                // #endregion
                
                audioData = await auditioBackendManager.getAudioData(audioFile);
                
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'WaveformAPI.tsx:512',message:'loadAudio: getAudioData succeeded',data:{hasAudioData:!!audioData,audioDataLength:audioData?.length||0},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
                // #endregion
            } catch (error: any) {
                // #region agent log
                fetch('http://127.0.0.1:7242/ingest/0c50d60c-ac54-4a09-a595-9aeb1181d095',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'WaveformAPI.tsx:515',message:'loadAudio: getAudioData failed',data:{error:error?.message,isAuthError:error?.isAuthError,isPermissionError:error?.isPermissionError,driveFileId:audioFile.driveFileId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
                // #endregion
                // Check if this is a permission or auth error
                if (error && (error.isPermissionError || error.isAuthError || error.isAuthCancelled)) {
                    console.error('❌ Error loading Google Drive file:', {
                        isAuthError: error.isAuthError,
                        isAuthCancelled: error.isAuthCancelled,
                        requiresReauth: error.requiresReauth,
                        message: error.message
                    });
                    setDrivePermissionError({
                        driveFileId: error.driveFileId,
                        driveFileUrl: error.driveFileUrl,
                        errorMessage: error.message,
                        isAuthError: error.isAuthError,
                        isAuthCancelled: error.isAuthCancelled,
                        requiresReauth: error.requiresReauth
                    });
                    return;
                }
                // Re-throw other errors
                throw error;
            }

            if (!audioData) {
                console.error('Audio data is null or undefined', {
                    fileId: audioFile.id,
                    fileName: audioFile.fileName,
                    driveFileId: audioFile.driveFileId,
                    youtubeUrl: audioFile.youtubeUrl,
                    hasData: !!audioFile.data
                });
                
                // For Drive files, check if we have a permission error
                if (audioFile.driveFileId && !drivePermissionError) {
                    // Try to set permission error if we can't load
                    setDrivePermissionError({
                        driveFileId: audioFile.driveFileId,
                        driveFileUrl: audioFile.driveFileUrl
                    });
                }
                return;
            }

            // Clear any permission errors if we successfully loaded
            setDrivePermissionError(null);

            try {
                const binary = convertDataURIToBinary(audioData);
                const audioBlob = new Blob([binary], {type: 'audio/mpeg'});

                // Create WaveSurfer instance
                const wavesurfer = WaveSurfer.create({
                    backend: 'MediaElement',
                    container: '#waveform',
                    waveColor: '#b2eee6', // Unplayed part of the waveform
                    progressColor: '#f97171', // Played part of the waveform
                    autoScroll: true,
                    autoCenter: true,
                    minPxPerSec: 0.5, // Reduced further to allow very small regions to render
                    fillParent: true,
                    height: 100,
                    barWidth: 2,
                    barGap: 1,
                    barRadius: 2,
                    plugins: [hover, regionPlugin, timeline] as any,
                    // Additional options for better region handling
                    interact: true,
                    hideScrollbar: false
                });

                wavesurferRef.current = wavesurfer;

                // Configure drag selection based on ownership (use calculatedIsOwner from file data)
                console.log('🔍 WaveformAPI - Setting up drag selection, isOwner:', calculatedIsOwner);
                if (calculatedIsOwner) {
                    regionPlugin.enableDragSelection({
                        color: 'rgba(0, 0, 0, 0.1)',
                    });
                    console.log('✅ WaveformAPI - Drag selection enabled for owner');
                } else {
                    // Explicitly ensure drag selection is disabled for non-owners
                    // Note: If drag selection was never enabled, this is a no-op, but it's explicit
                    console.log('🚫 WaveformAPI - Drag selection disabled for non-owner');
                }

                // Load audio blob
                setIsLoading(true);
                setWaveformIsReady(false);
                if (onWaveformReadyChange) {
                    onWaveformReadyChange(false);
                }
                if (onWaveformLoadingChange) {
                    onWaveformLoadingChange(true);
                }
                wavesurfer.loadBlob(new Blob([binary], {type : 'audio/mpeg'}));

                // Set up event listeners
                wavesurfer.on('ready', async () => {
                    // Critical log for waveform initialization
                    console.log("waveform is ready!");
                    
                    setIsLoading(false);
                    setWaveformIsReady(true);  // Set ready state
                    setDuration(wavesurfer.getDuration());  // Set duration
                    if (onWaveformReadyChange) {
                        onWaveformReadyChange(true);
                    }
                    if (onWaveformLoadingChange) {
                        onWaveformLoadingChange(false);
                    }
                    
                    if (!audioFileId) return;

                    try {
                        // Use the isOwner value calculated earlier in loadAudio (stored in ref)
                        const currentIsOwner = isOwnerRef.current;
                        console.log('🔍 WaveformAPI - Loading snippets with ownership:', {
                            currentIsOwner
                        });
                        
                        // Load snippets for the current audio file
                        await auditioBackendManager.loadSnippetsForCurrentAudioFile(
                            wavesurferRef.current,
                            regions,
                            regionRef,
                            setStartMarker,
                            setEndMarker,
                            zoomLevel,
                            () => {
                                // Callback to trigger re-render after snippets are loaded and active state is restored
                                setRenderTrigger(prev => prev + 1);
                            },
                            currentIsOwner  // Pass ownership status from file data
                        );
                        
                        // Ensure markers are cleared after loading snippets
                        setStartMarker(null);
                        setEndMarker(null);

                        // Set zoom level based on container width
                        if (document.getElementById('waveform')) {
                            const containerWidth = document.getElementById('waveform')!.clientWidth;
                            const duration = wavesurfer.getDuration();
                            
                            // Calculate optimal zoom to show all snippets
                            // First, get the snippets to determine the optimal zoom
                            try {
                                const snippetsResponse = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}`);
                                if (snippetsResponse.ok) {
                                    const snippets = await snippetsResponse.json();
                                    const actualSnippets = snippets?.filter((snippet: any) => 
                                        snippet.name !== "Start Marker" && snippet.name !== "End Marker"
                                    ) || [];
                                    
                                    if (actualSnippets.length > 0) {
                                        // Calculate the minimum snippet duration to ensure visibility
                                        const minSnippetDuration = Math.min(...actualSnippets.map((s: any) => 
                                            Number(s.endTime) - Number(s.startTime)
                                        ));
                                        
                                        // Calculate zoom based on minimum snippet duration
                                        // Ensure at least 50px width for the smallest snippet
                                        const minSnippetPixels = 50;
                                        const zoomForSnippets = minSnippetPixels / minSnippetDuration;
                                        
                                        // Also consider the overall fit
                                        const zoomForFit = containerWidth / duration;
                                        
                                        // Use the larger of the two to ensure both fit and snippet visibility
                                        const optimalZoom = Math.max(zoomForSnippets, zoomForFit);
                                        
                                        setZoomLevel(optimalZoom);
                                        setMinZoomLevel(optimalZoom);
                                        wavesurfer.zoom(optimalZoom);
                                        
                                        console.log('🔍 Set optimal zoom for snippets:', {
                                            minSnippetDuration,
                                            zoomForSnippets,
                                            zoomForFit,
                                            optimalZoom,
                                            snippetCount: actualSnippets.length
                                        });
                                    } else {
                                        // No snippets, use default calculation
                                        const zoom = containerWidth / duration;
                                        setZoomLevel(zoom);
                                        setMinZoomLevel(zoom);
                                        wavesurfer.zoom(zoom);
                                    }
                                } else {
                                    // Fallback to default calculation
                                    const zoom = containerWidth / duration;
                                    setZoomLevel(zoom);
                                    setMinZoomLevel(zoom);
                                    wavesurfer.zoom(zoom);
                                }
                            } catch (error) {
                                console.warn('Error calculating optimal zoom, using default:', error);
                                const zoom = containerWidth / duration;
                                setZoomLevel(zoom);
                                setMinZoomLevel(zoom);
                                wavesurfer.zoom(zoom);
                            }
                        }

                        // Debug: Log snippet count and visibility info
                        console.log('🔍 Waveform ready - snippet visibility debug:', {
                            audioFileId,
                            containerWidth: document.getElementById('waveform')?.clientWidth,
                            duration: wavesurfer.getDuration(),
                            zoomLevel: zoomLevel
                        });
                    } catch (error) {
                        console.error('Error loading snippets:', error);
                    }
                });

                // User interaction with waveform
                wavesurfer.on('seeking' as any, (progress) => {
                    console.log('User is interacting with the waveform:', progress);
                });

                wavesurfer.on('timeupdate', (currentTime) => {
                    setCurrentTime(currentTime);
                });

                wavesurferRef.current.on('play' as any, async () => {
                    setPlayPause(true);
                    // Track playback event (non-blocking)
                    // Check if we're in snippet mode - if so, track snippet_play, otherwise track_play
                    // Use a delay to ensure regionRef and loopOptionRef are set, and check current time against regions
                    setTimeout(() => {
                        // Check loop mode (both ref and state)
                        const loopOptionRefValue = loopOptionRef.current;
                        const loopOptionStateValue = loopOption;
                        const loopModeIsSnippet = loopOptionRefValue === 'snippet' || loopOptionStateValue === 'snippet';
                        
                        // Check if current time is within any region
                        let isInRegion = false;
                        let currentRegion: any = null;
                        let currentTime = 0;
                        let allRegions: any[] = [];
                        
                        if (wavesurferRef.current) {
                            currentTime = wavesurferRef.current.getCurrentTime();
                        }
                        
                        if (regions.current && Object.keys(regions.current).length > 0) {
                            allRegions = Object.values(regions.current);
                            allRegions.forEach((region: any) => {
                                if (currentTime >= region.start && currentTime <= region.end) {
                                    isInRegion = true;
                                    currentRegion = region;
                                }
                            });
                        }
                        
                        const hasActiveRegion = regionRef.current !== null;
                        const regionRefId = regionRef.current?.id;
                        const regionToTrack = regionRef.current || currentRegion;
                        const regionToTrackId = regionToTrack?.id;
                        
                        const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
                        
                        // Detailed logging
                        console.log('📊 ========== PLAY EVENT TRACKING DEBUG ==========');
                        console.log('📊 Loop Mode Check:', {
                            loopOptionRef: loopOptionRefValue,
                            loopOptionState: loopOptionStateValue,
                            loopModeIsSnippet: loopModeIsSnippet
                        });
                        console.log('📊 Region Check:', {
                            hasActiveRegion: hasActiveRegion,
                            regionRefId: regionRefId,
                            isInRegion: isInRegion,
                            currentTime: currentTime,
                            currentRegionId: currentRegion?.id,
                            currentRegionStart: currentRegion?.start,
                            currentRegionEnd: currentRegion?.end,
                            totalRegions: allRegions.length,
                            regionToTrackId: regionToTrackId,
                            hasRegionToTrack: !!regionToTrack
                        });
                        console.log('📊 Audio File:', {
                            audioFileId: audioFileId,
                            audioFileIdNum: audioFileIdNum,
                            isValid: audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)
                        });
                        
                        // Only track snippet_play if we have an active snippet/region AND loop mode is snippet
                        // If loop mode is snippet but no active snippet, track as track_play
                        if (loopModeIsSnippet && regionToTrack && audioFileIdNum && audioFileIdNum > 0) {
                            const regionId = regionToTrack.id; // This is the regionId (string), not the database ID
                            
                            // Look up the snippet database ID from the regionId
                            (async () => {
                                const snippet = await getSnippetByRegionId(regionId, audioFileIdNum);
                                
                                if (!snippet) {
                                    console.error('❌ Cannot track snippet play - snippet not found for regionId:', regionId);
                                    // Fall through to track_play - will be handled below
                                    return;
                                }
                                
                                // Use the database ID (snippet.id), not the regionId
                                const snippetIdNum = typeof snippet.id === 'string' ? parseInt(String(snippet.id), 10) : Number(snippet.id);
                                
                                console.log('📊 Snippet Tracking Decision:', {
                                    willTrackSnippet: true,
                                    snippetIdNum: snippetIdNum,
                                    regionId: regionId,
                                    snippetName: snippet.name,
                                    isValidSnippetId: snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)
                                });
                                
                                if (snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
                                    console.log('✅ TRACKING: snippet_play (play event) for audioFileId:', audioFileIdNum, 'snippetId (DB):', snippetIdNum, 'regionId:', regionId, 'snippet name:', snippet.name);
                                    let idToken: string | null = null;
                                    if (firebaseUser) {
                                        try {
                                            idToken = await firebaseUser.getIdToken();
                                        } catch (error) {
                                            console.warn('Could not get auth token for metrics:', error);
                                        }
                                    }
                                    metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
                                } else {
                                    console.error('❌ Snippet mode active but invalid snippetId:', snippetIdNum, 'region:', regionToTrack, 'snippet:', snippet);
                                    // Fall through to track_play - will be handled below
                                }
                            })();
                        } else {
                            // Track track_play if:
                            // - Not in snippet mode, OR
                            // - In snippet mode but no active snippet/region
                            console.log('📊 Track Tracking Decision:', {
                                willTrackTrack: true,
                                reason: loopModeIsSnippet 
                                    ? (regionToTrack ? 'snippet mode but invalid region' : 'snippet mode but no active snippet')
                                    : 'not in snippet mode',
                                loopModeIsSnippet: loopModeIsSnippet,
                                hasRegionToTrack: !!regionToTrack
                            });
                            
                            if (audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                                console.log('✅ TRACKING: track_play for audioFileId:', audioFileIdNum);
                                (async () => {
                                    let idToken: string | null = null;
                                    if (firebaseUser) {
                                        try {
                                            idToken = await firebaseUser.getIdToken();
                                        } catch (error) {
                                            // Silently fail - tracking works without token
                                            console.warn('Could not get auth token for metrics:', error);
                                        }
                                    }
                                    metricsService.trackTrackPlay(audioFileIdNum, user?.id || null, idToken);
                                })();
                            } else {
                                console.warn('⚠️ Skipping track playback tracking - invalid audioFileId:', audioFileId, 'converted:', audioFileIdNum);
                            }
                        }
                        console.log('📊 ========== END PLAY EVENT TRACKING DEBUG ==========');
                    }, 300); // Increased delay to ensure refs and regions are set after page refresh
                });

                wavesurferRef.current.on('pause' as any, () => {
                    setPlayPause(false);
                });

                wavesurferRef.current.on('finish' as any, async () => {
                    console.log('Track finished.');
                    if (loopOptionRef.current == 'track') {
                        // Track track loop event
                        const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
                        if (audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                            console.log('📊 Tracking track loop for audioFileId:', audioFileIdNum);
                            let idToken: string | null = null;
                            if (firebaseUser) {
                                try {
                                    idToken = await firebaseUser.getIdToken();
                                } catch (error) {
                                    console.warn('Could not get auth token for metrics:', error);
                                }
                            }
                            metricsService.trackTrackPlay(audioFileIdNum, user?.id || null, idToken);
                        }
                        wavesurferRef.current.play(0);
                    }
                });

                // Set up region event listeners
                // Track snippet playback when entering a region (covers both initial play and loops)
                // Track for any region that matches the active region, or if loop mode is snippet
                // Helper function to get snippet by regionId (with caching)
                const getSnippetByRegionId = async (regionId: string, audioFileIdNum: number): Promise<any | null> => {
                    try {
                        // Check cache first
                        const now = Date.now();
                        if (snippetsCacheRef.current && 
                            snippetsCacheRef.current.audioFileId === audioFileIdNum &&
                            (now - snippetsCacheRef.current.timestamp) < SNIPPETS_CACHE_TTL) {
                            console.log('📊 Using cached snippets for lookup');
                            const snippet = snippetsCacheRef.current.snippets.find((s: any) => 
                                s.regionId === regionId || String(s.regionId) === String(regionId)
                            );
                            return snippet || null;
                        }
                        
                        // Fetch snippets
                        console.log('📊 Fetching snippets for regionId lookup:', regionId);
                        const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileIdNum}`);
                        if (!response.ok) {
                            console.error('❌ Failed to fetch snippets for tracking:', response.status, response.statusText);
                            return null;
                        }
                        
                        const snippets = await response.json();
                        console.log('📊 Fetched snippets:', snippets.map((s: any) => ({ id: s.id, regionId: s.regionId, name: s.name })));
                        
                        // Update cache
                        snippetsCacheRef.current = {
                            audioFileId: audioFileIdNum,
                            snippets: snippets,
                            timestamp: now
                        };
                        
                        // Find snippet by regionId
                        const snippet = snippets.find((s: any) => 
                            s.regionId === regionId || String(s.regionId) === String(regionId)
                        );
                        
                        if (!snippet) {
                            console.error('❌ Snippet not found for regionId:', regionId, 'available regionIds:', snippets.map((s: any) => s.regionId));
                            return null;
                        }
                        
                        return snippet;
                    } catch (error) {
                        console.error('❌ Error looking up snippet by regionId:', error);
                        return null;
                    }
                };

                regionPlugin.on('region-in', (region: any) => {
                    console.log('📊 region-in event fired for region:', region.id, 'active region:', regionRef.current?.id, 'loop mode:', loopOptionRef.current);
                    
                    // Track if this region matches the active region, OR if snippet loop mode is enabled
                    const isActiveRegion = regionRef.current && regionRef.current.id === region.id;
                    const isSnippetLoopMode = loopOptionRef.current === 'snippet';
                    
                    if (isActiveRegion || isSnippetLoopMode) {
                        const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
                        const regionId = region.id; // This is the regionId (string), not the database ID
                        
                        // Look up the snippet database ID from the regionId
                        (async () => {
                            const snippet = await getSnippetByRegionId(regionId, audioFileIdNum);
                            
                            if (!snippet) {
                                console.error('❌ Cannot track snippet play - snippet not found for regionId:', regionId);
                                return;
                            }
                            
                            // Use the database ID (snippet.id), not the regionId
                            const snippetIdNum = typeof snippet.id === 'string' ? parseInt(String(snippet.id), 10) : Number(snippet.id);
                            
                            if (audioFileIdNum && audioFileIdNum > 0 && snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
                                console.log('✅ TRACKING snippet_play (region-in) for audioFileId:', audioFileIdNum, 'snippetId (DB):', snippetIdNum, 'regionId:', regionId, 'snippet name:', snippet.name);
                                let idToken: string | null = null;
                                if (firebaseUser) {
                                    try {
                                        idToken = await firebaseUser.getIdToken();
                                    } catch (error) {
                                        console.warn('Could not get auth token for metrics:', error);
                                    }
                                }
                                metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
                            } else {
                                console.error('❌ Invalid IDs for tracking:', { audioFileIdNum, snippetIdNum, regionId, snippet });
                            }
                        })();
                    } else {
                        console.log('📊 Skipping region-in tracking - not active region and not snippet loop mode');
                    }
                });

                regionPlugin.on('region-created', async (region: any) => {
                    console.log("region-created event fired! region:", region.id);

                    try {
                        // Store the region in regions.current so it can be tracked by other components
                        regions.current = {
                            ...regions.current,
                            [region.id]: region
                        };

                        // Get the region name from either the element's text content or the content property
                        let regionName = region.element ? region.element.textContent : 
                                       (typeof region.content === 'string' ? region.content : null);
                        
                        // If no name is found, use a default name
                        if (!regionName || regionName.trim() === '') {
                            regionName = 'New Snippet';
                        }

                        // Use the isOwner value from file data (stored in ref)
                        const currentIsOwner = isOwnerRef.current;
                        console.log("Setting options for region:", region.id, "with name:", regionName, "currentIsOwner:", currentIsOwner);

                        // Set region options - explicitly set drag/resize based on ownership
                        // This ensures the UI doesn't show drag handles for non-owners
                        const regionOptions = {
                            content: regionName,
                            contentEditable: currentIsOwner,  // Disable content editing for non-owners
                            drag: currentIsOwner,  // Disable dragging for non-owners
                            resize: currentIsOwner,  // Disable resizing for non-owners
                            color: 'rgba(85, 203, 205, 0.15)',
                        };
                        console.log('🔍 WaveformAPI - Setting region options:', regionOptions);
                        region.setOptions(regionOptions);
                        
                        // Verify the options were set correctly
                        setTimeout(() => {
                            console.log('🔍 WaveformAPI - Region options after setOptions:', {
                                id: region.id,
                                drag: region.drag,
                                resize: region.resize,
                                contentEditable: region.contentEditable
                            });
                        }, 100);

                        // Apply styling immediately after setting options
                        setTimeout(() => {
                            applyRegionContentStyling(region);
                            setupRegionContentObserver(region);
                        }, 10);

                        // Add a small delay to ensure region is fully initialized before saving
                        setTimeout(async () => {
                            try {
                                // Check if this region already exists in the database
                                console.log("Checking if region exists in database:", region.id);
                                const isExistingRegion = await auditioBackendManager.areRegionAndSnippetEquivalent(region, regions, audioFileId);
                                
                                if (!isExistingRegion) {
                                    // New region - save to backend and set as active
                                    console.log("New region created, saving to backend:", region.id);
                                    await auditioBackendManager.addOrUpdateSnippetFromRegion(region, audioFileId);
                                    
                                    // Verify the snippet exists in the database before setting as active
                                    console.log("Verifying snippet exists in database:", region.id);
                                    const verificationResult = await auditioBackendManager.areRegionAndSnippetEquivalent(region, regions, audioFileId);
                                    
                                    if (verificationResult) {
                                        console.log("Snippet verified in database, setting as active:", region.id);
                                        await auditioBackendManager.updateActiveRegion(regionRef, region.id, regions, audioFileId);
                                    } else {
                                        console.log("Snippet not found in database, skipping active set:", region.id);
                                    }
                                } else {
                                    console.log("Region already exists in database, skipping save:", region.id);
                                }
                                
                                // Force a re-render for all regions to ensure UI updates
                                console.log("Triggering re-render for region:", region.id);
                                setRenderTrigger(prev => prev + 1);
                            } catch (error) {
                                console.error('Error in region creation backend operations:', error);
                                // Still trigger re-render even if backend operations fail
                                setRenderTrigger(prev => prev + 1);
                            }
                        }, 100); // 100ms delay to ensure region is fully initialized

                    } catch (error) {
                        console.error('Error in region-created event handler:', error);
                        // Still trigger re-render even if there's an error
                        setRenderTrigger(prev => prev + 1);
                    }
                });

                regionPlugin.on('region-clicked', (region: any) => {
                    console.log("region-clicked! region: ", region);
                    
                    const updateActiveRegionOnClick = async () => {
                        try {
                            await auditioBackendManager.updateActiveRegion(regionRef, region.id, regions, audioFileId);
                            setRenderTrigger(prev => prev + 1);
                        } catch (error) {
                            console.error('Error updating active region on click:', error);
                        }
                    };
                    
                    updateActiveRegionOnClick();
                });

                regionPlugin.on('region-out', () => {
                    console.log('region-out entered!');
                });

                regionPlugin.on('region-updated', (region: any) => {
                    console.log('region-updated event fired! region:', region);
                    console.log('Current regions.current:', regions.current);
                    
                    // Only allow updates for owners (non-owners shouldn't be able to trigger this if drag/resize are disabled)
                    if (!isOwnerRef.current) {
                        console.log('Non-owner attempted to update region, ignoring update');
                        return;
                    }
                    
                    const updateRegion = async () => {
                        try {
                            await auditioBackendManager.addOrUpdateSnippetFromRegion(region, audioFileId);
                            setRenderTrigger(prev => prev + 1);
                        } catch (error) {
                            console.error('Error updating region:', error);
                        }
                    };
                    
                    updateRegion();
                });

                // Handle region finish event for snippet looping
                regionPlugin.on('region-out', (region: any) => {
                    console.log('region-out event fired! region:', region.id);
                    
                    // Prevent infinite loop - if we're already restarting, ignore this event
                    if (isRestartingRegionRef.current) {
                        console.log('⚠️ Already restarting region, ignoring region-out event to prevent loop');
                        return;
                    }
                    
                    // If snippet looping is enabled and this is the active region, restart it
                    if (loopOptionRef.current === 'snippet' && 
                        regionRef.current && 
                        regionRef.current.id === region.id) {
                        console.log('Restarting snippet for loop:', region.id);
                        isRestartingRegionRef.current = true;
                        
                        // Note: Tracking is now handled by region-in event, which fires when region.play() is called
                        
                        // Pause before region.play() to ensure clean state
                        // region.play() handles seeking to region start internally - no need to pre-seek
                        if (wavesurferRef.current && regionRef.current && regionRef.current.play) {
                            // Pause first to stop any playback
                            if (typeof wavesurferRef.current.pause === 'function') {
                                wavesurferRef.current.pause();
                            }
                            
                            // region.play() will handle seeking to region start internally
                            // No need to pre-seek - that would be duplicate logic and could cause issues
                            regionRef.current.play();
                            console.log('✅ Region loop restart - region.play() called (will seek internally)');
                            
                            // Reset flag after a delay to allow next loop
                            setTimeout(() => {
                                isRestartingRegionRef.current = false;
                            }, 200); // Delay to ensure region.play() has started
                        } else {
                            isRestartingRegionRef.current = false;
                        }
                    }
                });
            } catch (error) {
                console.error('Error loading audio:', error);
            }
        } catch (error) {
            console.error('Error loading audio:', error);
        }
    };

    const setMarkers = () => {
        if (startMarker && regions.current[startMarker.id]) {
            startMarker.setOptions({
                content: "Start Marker",
                resize: false,
                drag: true,
                color: 'rgba(0, 0, 0, 0.1)',
            });
        }

        if (endMarker && regions.current[endMarker.id]) {
            endMarker.setOptions({
                content: "End Marker",
                resize: false,
                drag: true,
                color: 'rgba(0, 0, 0, 0.1)',
            });
        }
    };

    // Create or update markers
    const createOrUpdateStartMarker = async () => {
        const currentTimeLocal = wavesurferRef.current.getCurrentTime();

        if (startMarker && regions.current[startMarker.id]) {
            startMarker.setOptions({
                start: currentTimeLocal,
            });
            // Update the marker in the database via region-updated event
        }
        else {
            const regionsPlugin = wavesurferRef.current.getActivePlugins()[1] as RegionsPlugin;
            const newStartMarker = regionsPlugin.addRegion({
                start: currentTimeLocal,
                content: "Start Marker",
                resize: false,
                drag: true,
                color: 'rgba(0, 0, 0, 0.1)',
            });
            
            // Store the marker in regions.current
            regions.current = {
                ...regions.current,
                [newStartMarker.id]: newStartMarker
            };
            
            setStartMarker(newStartMarker);
            // The region-created event handler will save to database
        }
        setMarkers();
    };

    const createOrUpdateEndMarker = async () => {
        const currentTimeLocal = wavesurferRef.current.getCurrentTime();
        let currentEndMarker = endMarker;

        if (endMarker && regions.current[endMarker.id]) {
            endMarker.setOptions({
                start: currentTimeLocal,
            });
            // Update the marker in the database via region-updated event
        }
        else {
            const regionsPlugin = wavesurferRef.current.getActivePlugins()[1] as RegionsPlugin;
            const newEndMarker = regionsPlugin.addRegion({
                start: currentTimeLocal,
                content: "End Marker",
                resize: false,
                drag: true,
                color: 'rgba(0, 0, 0, 0.1)',
            });
            
            // Store the marker in regions.current
            regions.current = {
                ...regions.current,
                [newEndMarker.id]: newEndMarker
            };
            
            currentEndMarker = newEndMarker;
            setEndMarker(newEndMarker);
            // The region-created event handler will save to database
        }
        setMarkers();
        
        // Automatically create region if both start and end markers exist
        setTimeout(async () => {
            if (startMarker && currentEndMarker) {
                try {
                    const regionsPlugin = wavesurferRef.current.getActivePlugins()[1] as RegionsPlugin;
                    const newRegion = regionsPlugin.addRegion({
                        start: Math.min(startMarker.start, currentEndMarker.start),
                        end: Math.max(startMarker.start, currentEndMarker.start),
                        content: "New Snippet",
                        contentEditable: true,
                        color: 'rgba(85, 203, 205, 0.15)',
                    });

                    // Store the region in regions.current
                    regions.current = {
                        ...regions.current,
                        [newRegion.id]: newRegion
                    };

                    // Wait for the region to be fully created and saved to backend
                    // The region-created event handler will handle the backend operations
                    // We'll wait a bit longer to ensure the region creation is complete
                    setTimeout(async () => {
                        try {
                            // Check if the region was successfully created by verifying it exists in regions.current
                            if (regions.current[newRegion.id]) {
                                console.log('✅ Region created successfully, removing markers');
                                
                                // Remove markers from UI and database only after successful region creation
                                if (startMarker && startMarker.remove && typeof startMarker.remove === 'function') {
                                    // Delete from database first
                                    try {
                                        await auditioBackendManager.deleteSnippet(startMarker.id, regions, regionRef, audioFileId);
                                    } catch (error) {
                                        console.warn('Failed to delete start marker from database:', error);
                                    }
                                    
                                    // Then remove from UI
                                    try {
                                        startMarker.remove();
                                    } catch (error) {
                                        console.warn('Error removing start marker from UI:', error);
                                    }
                                    if (regions.current && regions.current[startMarker.id]) {
                                        delete regions.current[startMarker.id];
                                    }
                                }
                                
                                if (currentEndMarker && currentEndMarker.remove && typeof currentEndMarker.remove === 'function') {
                                    // Delete from database first
                                    try {
                                        await auditioBackendManager.deleteSnippet(currentEndMarker.id, regions, regionRef, audioFileId);
                                    } catch (error) {
                                        console.warn('Failed to delete end marker from database:', error);
                                    }
                                    
                                    // Then remove from UI
                                    try {
                                        currentEndMarker.remove();
                                    } catch (error) {
                                        console.warn('Error removing end marker from UI:', error);
                                    }
                                    if (regions.current && regions.current[currentEndMarker.id]) {
                                        delete regions.current[currentEndMarker.id];
                                    }
                                }
                                
                                // Clear marker state
                                setStartMarker(null);
                                setEndMarker(null);
                                
                                // Apply styling to the new region
                                setTimeout(() => {
                                    applyRegionContentStyling(newRegion);
                                    setupRegionContentObserver(newRegion);
                                    updateRegionColors(regionRef.current?.id || null);
                                }, 100);
                                
                                setRenderTrigger(prev => prev + 1);
                            } else {
                                console.log('❌ Region creation failed, keeping markers');
                            }
                        } catch (error) {
                            console.error('Error in marker cleanup after region creation:', error);
                            // If there's an error, don't delete the markers
                        }
                    }, 300); // Wait longer for region creation to complete
                    
                } catch (error) {
                    console.error('Error creating region:', error);
                    // If region creation fails, don't delete the markers
                }
            }
        }, 100); // Small delay to ensure markers are properly set
    };

    // Handle loop option changes
    const handleLoopOptionChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedLoopOption = e.target.value;
        setLoopOption(selectedLoopOption);
        loopOptionRef.current = selectedLoopOption;

        // Track track play when switching to track loop mode and track is currently playing
        if (selectedLoopOption === 'track' && playPause && wavesurferRef.current) {
            const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
            if (audioFileIdNum && audioFileIdNum > 0 && !isNaN(audioFileIdNum)) {
                console.log('📊 Tracking track play (switched to track loop mode while playing) for audioFileId:', audioFileIdNum);
                (async () => {
                    let idToken: string | null = null;
                    if (firebaseUser) {
                        try {
                            idToken = await firebaseUser.getIdToken();
                        } catch (error) {
                            console.warn('Could not get auth token for metrics:', error);
                        }
                    }
                    metricsService.trackTrackPlay(audioFileIdNum, user?.id || null, idToken);
                })();
            }
        }

        // Track snippet play when switching to snippet loop mode and a snippet is active
        if (selectedLoopOption === 'snippet' && regionRef.current && playPause) {
            const audioFileIdNum = typeof audioFileId === 'string' ? parseInt(audioFileId, 10) : audioFileId;
            const snippetIdNum = regionRef.current.id ? (typeof regionRef.current.id === 'string' ? parseInt(String(regionRef.current.id), 10) : Number(regionRef.current.id)) : null;
            
            if (audioFileIdNum && audioFileIdNum > 0 && snippetIdNum && snippetIdNum > 0 && !isNaN(snippetIdNum)) {
                console.log('📊 Tracking snippet play (switched to snippet loop mode while playing) for audioFileId:', audioFileIdNum, 'snippetId:', snippetIdNum);
                (async () => {
                    let idToken: string | null = null;
                    if (firebaseUser) {
                        try {
                            idToken = await firebaseUser.getIdToken();
                        } catch (error) {
                            console.warn('Could not get auth token for metrics:', error);
                        }
                    }
                    metricsService.trackSnippetPlay(audioFileIdNum, snippetIdNum, user?.id || null, idToken);
                })();
            }
        }

        if (selectedLoopOption === 'snippet') {
            await handleSnippetLoopActivation();
        }

        setRenderTrigger(prev => prev + 1);
    };

    // Handle snippet loop activation logic
    const handleSnippetLoopActivation = async () => {
        try {
            // Get current snippets for this audio file
            const response = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}`);
            if (!response.ok) {
                console.error('Failed to fetch snippets for loop activation');
                return;
            }
            
            const snippets = await response.json();
            const currentTime = wavesurferRef.current?.getCurrentTime() || 0;
            
            console.log('🔍 Snippet loop activation - current time:', currentTime, 'snippets:', snippets);
            
            // Check if there's an active snippet
            const activeSnippet = snippets.find((s: any) => s.isActive);
            
            if (activeSnippet) {
                // Case 1: There is an active snippet - seek to its beginning and play
                console.log('🔍 Active snippet found:', activeSnippet.name, 'seeking to start time:', activeSnippet.startTime);
                
                // Set this as the active region
                if (regions.current && regions.current[activeSnippet.regionId]) {
                    const region = regions.current[activeSnippet.regionId];
                    regionRef.current = region;
                    
                    // Seek to the beginning of the snippet
                    wavesurferRef.current?.seekTo(activeSnippet.startTime);
                    
                    // Start playing the snippet
                    region.play();
                } else {
                    // If region not found in current regions, seek and play normally
                    wavesurferRef.current?.seekTo(activeSnippet.startTime);
                    wavesurferRef.current?.play();
                }
            } else {
                // Case 2: No active snippet - check if current regionRef matches any snippet
                // This handles the case where the page was refreshed and regionRef is set but backend doesn't know
                if (regionRef.current && regions.current) {
                    const currentRegionId = regionRef.current.id;
                    const matchingSnippet = snippets.find((s: any) => s.regionId === currentRegionId);
                    
                    if (matchingSnippet) {
                        console.log('🔍 Current region matches snippet, setting as active:', matchingSnippet.name);
                        
                        // Set this snippet as active in the backend
                        await setSnippetAsActive(matchingSnippet.id);
                        
                        // Seek to the beginning of the snippet
                        wavesurferRef.current?.seekTo(matchingSnippet.startTime);
                        
                        // Start playing the snippet
                        regionRef.current.play();
                        return;
                    }
                }
                
                // Case 3: No active snippet and no matching region - find the nearest snippet and activate it
                const nearestSnippet = findNearestSnippet(snippets, currentTime);
                
                if (nearestSnippet) {
                    console.log('🔍 No active snippet, activating nearest snippet:', nearestSnippet.name);
                    
                    // Set this snippet as active in the backend
                    await setSnippetAsActive(nearestSnippet.id);
                    
                    // Set this as the active region
                    if (regions.current && regions.current[nearestSnippet.regionId]) {
                        const region = regions.current[nearestSnippet.regionId];
                        regionRef.current = region;
                        
                        // Seek to the beginning of the snippet
                        wavesurferRef.current?.seekTo(nearestSnippet.startTime);
                        
                        // Start playing the snippet
                        region.play();
                    } else {
                        // If region not found in current regions, seek and play normally
                        wavesurferRef.current?.seekTo(nearestSnippet.startTime);
                        wavesurferRef.current?.play();
                    }
                } else {
                    console.log('🔍 No snippets found for this audio file');
                    // No snippets available, just start playing from current position
                    wavesurferRef.current?.play();
                }
            }
        } catch (error) {
            console.error('Error in snippet loop activation:', error);
            // Fallback: just start playing from current position
            wavesurferRef.current?.play();
        }
    };

    // Find the nearest snippet based on current time
    const findNearestSnippet = (snippets: any[], currentTime: number) => {
        if (!snippets || snippets.length === 0) return null;
        
        // Sort snippets by start time
        const sortedSnippets = [...snippets].sort((a, b) => a.startTime - b.startTime);
        
        // Case 1: Check if current time is between any snippet's start and end time
        const currentSnippet = sortedSnippets.find(snippet => 
            currentTime >= snippet.startTime && currentTime <= snippet.endTime
        );
        
        if (currentSnippet) {
            // If multiple snippets contain current time, pick the one with earlier start time
            const overlappingSnippets = sortedSnippets.filter(snippet => 
                currentTime >= snippet.startTime && currentTime <= snippet.endTime
            );
            return overlappingSnippets[0]; // Already sorted by start time, so first is earliest
        }
        
        // Case 2: Current time is not between any snippet
        // Find the next snippet whose start time is nearest to current time
        const futureSnippets = sortedSnippets.filter(snippet => snippet.startTime > currentTime);
        
        if (futureSnippets.length > 0) {
            // Find the snippet with start time closest to current time
            return futureSnippets.reduce((nearest, current) => 
                Math.abs(current.startTime - currentTime) < Math.abs(nearest.startTime - currentTime) 
                    ? current 
                    : nearest
            );
        } else {
            // No future snippets, find the nearest snippet before current time
            const pastSnippets = sortedSnippets.filter(snippet => snippet.startTime <= currentTime);
            if (pastSnippets.length > 0) {
                // Return the snippet with the latest start time (closest to current time)
                return pastSnippets[pastSnippets.length - 1];
            }
        }
        
        return null;
    };

    // Set a snippet as active in the backend
    const setSnippetAsActive = async (snippetId: number) => {
        try {
            // First, set all snippets as inactive
            const snippets = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}`);
            if (snippets.ok) {
                const snippetList = await snippets.json();
                for (const snippet of snippetList) {
                    if (snippet.id !== snippetId) {
                        await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}/${snippet.regionId}/inactive`, {
                            method: 'PUT'
                        });
                    }
                }
            }
            
            // Then set the target snippet as active
            const targetSnippet = await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}`);
            if (targetSnippet.ok) {
                const snippetList = await targetSnippet.json();
                const target = snippetList.find((s: any) => s.id === snippetId);
                if (target) {
                    await fetch(`${auditioBackendManager.getBaseUrl()}/snippets/${audioFileId}/${target.regionId}/active`, {
                        method: 'PUT'
                    });
                }
            }
        } catch (error) {
            console.error('Error setting snippet as active:', error);
        }
    };

    // Handle play/pause
    const handlePlayPause = () => {

        const currentTimeLocal = wavesurferRef.current.getCurrentTime();

        if (regionRef.current && 
            loopOptionRef.current == 'snippet' &&
            !playPause &&
            !(currentTimeLocal >= regionRef.current.start && currentTimeLocal <= regionRef.current.end)
        ) {
            regionRef.current.play();
        }
        else {
            wavesurferRef.current.playPause();
        }
    };

    // Seek through audio
    const seek = (seconds: number) => {
        const currentTimeLocal = wavesurferRef.current.getCurrentTime();

        if (!wavesurferRef.current) {
            return;
        }

        if (seconds < 0 && currentTimeLocal + seconds < 0) {
            wavesurferRef.current.skip(-1 * currentTimeLocal);
        }
        else if (seconds > 0 && currentTimeLocal + seconds > duration) {
            wavesurferRef.current.skip(duration);
        }
        else {
            wavesurferRef.current.skip(seconds);
        }
    };

    // Handle zoom changes
    const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setZoomLevel(parseInt(e.target.value));
        wavesurferRef.current.zoom(parseInt(e.target.value));
    };

    const zoomStep = (step: number) => {
        const currentStep = zoomLevel;
        setZoomLevel(currentStep + step);
        wavesurferRef.current.zoom(currentStep + step);
    };

    // Handle playback rate changes
    const handlePlaybackRateChange = (event: SelectChangeEvent<number>) => {
        let localPlaybackRate = Number(event.target.value);
        setPlaybackRate(localPlaybackRate);
        wavesurferRef.current.setPlaybackRate(localPlaybackRate);
    };

    // Keyboard shortcuts
    useShortcut(' ', () => {
        handlePlayPause();
    });

    useShortcut('z', () => {
        seek(-30);
    });

    useShortcut('x', () => {
        seek(-10);
    });

    useShortcut('c', () => {
        seek(-5);
    });

    useShortcut('v', () => {
        seek(5);
    });

    useShortcut('b', () => {
        seek(10);
    });

    useShortcut('n', () => {
        seek(30);
    });

    useShortcut('s', () => {
        createOrUpdateStartMarker();
    });

    useShortcut('e', () => {
        createOrUpdateEndMarker();
    });



    // Generate twinkling stars
    useEffect(() => {
        console.log('Generating stars...');
        const starsContainer = document.querySelector('.stars');
        console.log('Stars container found:', !!starsContainer);
        console.log('Stars container element:', starsContainer);
        
        if (starsContainer) {
            // Clear existing stars
            starsContainer.innerHTML = '';
            
            const starCount = 60; // Increased star count
            console.log('Creating', starCount, 'stars');
            
            for (let i = 0; i < starCount; i++) {
                const star = document.createElement('div');
                star.className = 'star';
                
                // Random position
                const top = Math.random() * 100;
                const left = Math.random() * 100;
                
                // Random size - make stars more varied
                const size = 1 + Math.random() * 2;
                
                star.style.top = `${top}%`;
                star.style.left = `${left}%`;
                star.style.width = `${size}px`;
                star.style.height = `${size}px`;
                
                // Explicitly set animation properties
                star.style.animation = `twinkle 4s infinite ease-in-out`;
                star.style.animationDelay = `${Math.random() * 4}s`;
                
                starsContainer.appendChild(star);
            }
            console.log('Stars created successfully');
        } else {
            console.log('Stars container not found, retrying in 500ms...');
            setTimeout(() => {
                const retryContainer = document.querySelector('.stars');
                if (retryContainer) {
                    console.log('Found stars container on retry');
                    // Generate stars here too
                    retryContainer.innerHTML = '';
                    const starCount = 60;
                    for (let i = 0; i < starCount; i++) {
                        const star = document.createElement('div');
                        star.className = 'star';
                        const top = Math.random() * 100;
                        const left = Math.random() * 100;
                        const size = 1 + Math.random() * 2;
                        star.style.top = `${top}%`;
                        star.style.left = `${left}%`;
                        star.style.width = `${size}px`;
                        star.style.height = `${size}px`;
                        star.style.animation = `twinkle 4s infinite ease-in-out`;
                        star.style.animationDelay = `${Math.random() * 4}s`;
                        retryContainer.appendChild(star);
                    }
                } else {
                    console.log('Stars container still not found after retry');
                }
            }, 500);
        }
    }, []); // Run on component mount, not just when audioFileId changes

    // Load audio when audioFileId changes
    useEffect(() => {
        // Critical log for debugging file loading issues
        console.log("Loading audio for file ID:", audioFileId);

        if (!audioFileId) {
            // If no audioFileId, cleanup
            if (wavesurferRef.current) {
                console.log("Cleaning up - no audioFileId");
                wavesurferRef.current.destroy();
                regions.current = null;
                if (regionRef.current) {
                    regionRef.current = null;
                }
            }
            // Clean up markers
            setStartMarker(null);
            setEndMarker(null);
            setIsLoading(false);
            setWaveformIsReady(false);
            if (onWaveformReadyChange) {
                onWaveformReadyChange(false);
            }
            if (onWaveformLoadingChange) {
                onWaveformLoadingChange(false);
            }
            return;
        }

        // Reset loading state when audioFileId changes
        setIsLoading(true);
        setWaveformIsReady(false);
        if (onWaveformReadyChange) {
            onWaveformReadyChange(false);
        }
        if (onWaveformLoadingChange) {
            onWaveformLoadingChange(true);
        }

        // Clean up existing markers before loading new audio
        if (startMarker && startMarker.remove && typeof startMarker.remove === 'function') {
            try {
                startMarker.remove();
            } catch (error) {
                console.warn('Error removing start marker:', error);
            }
        }
        if (endMarker && endMarker.remove && typeof endMarker.remove === 'function') {
            try {
                endMarker.remove();
            } catch (error) {
                console.warn('Error removing end marker:', error);
            }
        }
        setStartMarker(null);
        setEndMarker(null);

        // Load the audio file
        console.log("Initializing waveform for audioFileId:", audioFileId);
        loadAudio();

        // Cleanup function
        return () => {
            if (wavesurferRef.current) {
                console.log("Cleanup - destroying waveform");
                wavesurferRef.current.destroy();
            }
            // Clean up markers on unmount
            if (startMarker && startMarker.remove && typeof startMarker.remove === 'function') {
                try {
                    startMarker.remove();
                } catch (error) {
                    console.warn('Error removing start marker on unmount:', error);
                }
            }
            if (endMarker && endMarker.remove && typeof endMarker.remove === 'function') {
                try {
                    endMarker.remove();
                } catch (error) {
                    console.warn('Error removing end marker on unmount:', error);
                }
            }
            setStartMarker(null);
            setEndMarker(null);
        };
    }, [audioFileId]);

    // Listen for custom events to set loop option from other components
    useEffect(() => {
        const handleSetLoopOption = async (event: Event) => {
            const customEvent = event as CustomEvent;
            const { loopOption } = customEvent.detail;
            setLoopOption(loopOption);
            loopOptionRef.current = loopOption;
            
            if (loopOption === 'snippet') {
                await handleSnippetLoopActivation();
            }
            
            setRenderTrigger(prev => prev + 1);
        };

        window.addEventListener('setLoopOption', handleSetLoopOption);
        
        return () => {
            window.removeEventListener('setLoopOption', handleSetLoopOption);
        };
    }, []);

    // Update progress bar based on current time
    useEffect(() => {
        if (duration > 0) {
            const progressPercentage = (currentTime / duration) * 100;
            const timeDisplayElement = document.querySelector('.time-display') as HTMLElement;
            if (timeDisplayElement) {
                timeDisplayElement.style.setProperty('--progress-percentage', `${progressPercentage}%`);
            }
        }
    }, [currentTime, duration]);

    // Show permission error UI if there's a Drive permission error
    if (drivePermissionError) {
        const isAuthError = drivePermissionError.isAuthError || drivePermissionError.requiresReauth;
        const isAuthCancelled = drivePermissionError.isAuthCancelled;
        const errorMessage = drivePermissionError.errorMessage || 'You don\'t have access to this Google Drive file';
        
        const handleReauthenticate = async () => {
            try {
                const { GoogleDriveService } = await import('./services/GoogleDriveService');
                const driveService = new GoogleDriveService();
                await driveService.getAccessToken();
                // Clear the error and reload
                setDrivePermissionError(null);
                // Reload the audio file
                window.location.reload();
            } catch (error) {
                console.error('Failed to re-authenticate:', error);
                // Error will be shown by the Google Drive service
            }
        };
        
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '20px',
                    p: 4,
                }}
            >
                <Paper sx={{ p: 3, maxWidth: 500, textAlign: 'center' }}>
                    <Alert 
                        severity={isAuthError || isAuthCancelled ? "info" : "warning"} 
                        sx={{ mb: 2 }}
                    >
                        {isAuthError || isAuthCancelled 
                            ? 'Google Drive Authentication Required'
                            : 'You don\'t have access to this Google Drive file'
                        }
                    </Alert>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        {isAuthError || isAuthCancelled
                            ? errorMessage || 'Please sign in to Google Drive to access this file. Make sure you\'re using the same Google account that owns the file.'
                            : errorMessage || 'The owner of this file needs to share it with you, or make it publicly accessible.'
                        }
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {(isAuthError || isAuthCancelled || drivePermissionError.requiresReauth) && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleReauthenticate}
                            >
                                Sign in to Google Drive
                            </Button>
                        )}
                        {drivePermissionError.driveFileUrl && !isAuthError && !isAuthCancelled && (
                            <Button
                                variant="contained"
                                onClick={() => window.open(drivePermissionError.driveFileUrl, '_blank')}
                            >
                                Request Access in Google Drive
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => window.location.href = '/'}
                        >
                            Go to Home
                        </Button>
                    </Box>
                </Paper>
            </Box>
        );
    }

    return (
        <Box className="player-container" sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Stars/glitter effect */}
            <div className="stars" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: 'none' }}></div>
            
            <div 
                id="waveform" 
                ref={waveformContainerRef}
                style={{ 
                    touchAction: 'none', // Prevent default touch behaviors
                    userSelect: 'none', // Prevent text selection during zoom
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none'
                }}
            >
                {/* Zoom indicator for touch gestures */}
                {isZooming && (
                    <div className="zoom-indicator show">
                        {Math.round(zoomLevel)}x
                    </div>
                )}
                
                {/* Touch gesture hint for mobile */}
                {showTouchHint && (
                    <div className="touch-zoom-hint show">
                        Pinch to zoom • Use two fingers
                    </div>
                )}
            </div>

            {waveformIsReady && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem',
                        position: 'relative',
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0.5rem 0',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                            }}
                        >
                            <PlaybackSpeedDropdown
                                playbackRate={playbackRate}
                                onPlaybackRateChange={(rate) => handlePlaybackRateChange({ target: { value: rate } } as any)}
                            />
                        </Box>
                        
                        <Box className="time-display">
                            <span>{Math.floor(currentTime / 60).toString().padStart(2, '0')}:{(currentTime % 60).toFixed(0).padStart(2, '0')}</span> / <span>{Math.floor(duration / 60).toString().padStart(2, '0')}:{(duration % 60).toFixed(0).padStart(2, '0')}</span>
                        </Box>
                        
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                            }}
                        >
                            <div className="zoom-controls" id="zoom-controls">
                                <IconButton 
                                    className="control-btn"
                                    aria-label='Zoom Out' 
                                    onClick={() => zoomStep(-5)}
                                >
                                    <ZoomOut />
                                </IconButton>
                                <IconButton 
                                    className="control-btn"
                                    aria-label='Zoom In' 
                                    onClick={() => zoomStep(+5)}
                                >
                                    <ZoomIn />
                                </IconButton>
                            </div>
                        </Box>
                    </Box>
                    
                    <Box
                        className="media-controls-row"
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: { xs: 'center', md: 'space-between' },
                            alignItems: 'center', 
                            mb: 2, 
                            position: 'relative',
                            zIndex: 1,
                            gap: { xs: '1rem', md: '0.5rem' },
                            textAlign: { xs: 'center', md: 'left' },
                            width: '100%'
                        }}
                    >
                        <Box
                            className="time-jump-controls"
                            id="time-jump-controls"
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '0.5rem',
                                width: { xs: '100%', md: 'auto' }
                            }}
                        >
                            <button className="time-jump-btn backward" onClick={() => seek(-30)}>
                                <FastRewind />
                                30s
                            </button>
                            <button className="time-jump-btn backward" onClick={() => seek(-10)}>
                                <FastRewind />
                                10s
                            </button>
                            <button className="time-jump-btn backward" onClick={() => seek(-5)}>
                                <FastRewind />
                                5s
                            </button>
                            <button className="time-jump-btn forward" onClick={() => seek(5)}>
                                <FastForward />
                                5s
                            </button>
                            <button className="time-jump-btn forward" onClick={() => seek(10)}>
                                <FastForward />
                                10s
                            </button>
                            <button className="time-jump-btn forward" onClick={() => seek(30)}>
                                <FastForward />
                                30s
                            </button>
                        </Box>
                        
                        <Box
                            className="center-controls"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: { xs: '100%', md: 'auto' },
                                position: { xs: 'static', md: 'absolute' },
                                left: { xs: 'auto', md: '50%' },
                                transform: { xs: 'none', md: 'translateX(-50%)' },
                                zIndex: 10,
                                '@media (min-width: 1160px)': {
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 'auto'
                                },
                                '@media (max-width: 1159px)': {
                                    position: 'static',
                                    left: 'auto',
                                    transform: 'none',
                                    width: '100%',
                                    justifyContent: 'center'
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                }}
                            >
                            {isOwner && (
                                <Tooltip title="Start Marker">
                                    <button 
                                        className="speed-control"
                                        id="start-marker-btn"
                                        onClick={createOrUpdateStartMarker}
                                    >
                                        Start
                                    </button>
                                </Tooltip>
                            )}
                            
                            <Tooltip title="Play">
                                <div className="play-button-container">
                                    <button 
                                        className="play-btn"
                                        onClick={handlePlayPause}
                                    > 
                                        { playPause ? <Pause /> : <PlayArrow /> }
                                    </button>
                                </div>
                            </Tooltip>
                            
                            {isOwner && (
                                <Tooltip title="End Marker">
                                    <button 
                                        className="speed-control"
                                        id="end-marker-btn"
                                        onClick={createOrUpdateEndMarker}
                                    >
                                        End
                                    </button>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                        
                        <Box
                            className="loop-controls"
                            id="loop-controls"
                            sx={{ 
                                display: 'flex', 
                                justifyContent: { xs: 'center', md: 'flex-end' },
                                alignItems: 'center',
                                width: { xs: '100%', md: 'auto' },
                                flexDirection: 'row'
                            }}
                        >
                            <span className="loop-label">Loop:</span>
                            <div className="loop-selector" id="loop-selector">
                                <div 
                                    className={`loop-option ${loopOption === 'snippet' ? 'active' : ''}`}
                                    onClick={() => handleLoopOptionChange({ target: { value: 'snippet' } } as any)}
                                >
                                    Snippet
                                </div>
                                <div 
                                    className={`loop-option ${loopOption === 'track' ? 'active' : ''}`}
                                    onClick={() => handleLoopOptionChange({ target: { value: 'track' } } as any)}
                                >
                                    Track
                                </div>
                                <div 
                                    className={`loop-option ${loopOption === '' ? 'active' : ''}`}
                                    onClick={() => handleLoopOptionChange({ target: { value: '' } } as any)}
                                >
                                    None
                                </div>
                            </div>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WaveformAPI; 